import {
  type EntityReadStorage,
  FirstDownloadEnd,
  FIRST_DOWNLOAD_NOT_RUNNING,
  FIRST_DOWNLOAD_SESSION_ENDED,
  waitForFirstDownload,
} from 'serviceWorker/channelServers/entitySource';
import { syncWhenRequestsAllowed } from 'serviceWorker/channelServers/runWhenRequestsAllowed';
import { syncWithPermissions } from 'serviceWorker/channelServers/serveEntityRequests';
import { RESTARTS_FOR_REPLACED_DATA } from 'serviceWorker/const/dataReplacement';
import { swSignals } from 'serviceWorker/events/swSignals';
import { syncAllFieldValues } from 'serviceWorker/services/fieldValues/fieldValueSources';
import { isPullRunning } from 'serviceWorker/services/factories/runningPulls';
import { canSyncInspections } from 'serviceWorker/services/inspections';
import { captureCollectionScope } from 'serviceWorker/helpers/dataScope';
import { isNoProjectAccessPullFailure } from 'serviceWorker/helpers/broadcastPullError';
import { ProjectDataDownloadError } from 'shared/domain/projectData/projectDataDownloadError';
import {
  isInspectionDataKey,
  ProjectDataDownloadFailure,
  ProjectDataDownloadProgress,
  ProjectDataDownloadStatus,
  ProjectDataKey,
} from 'shared/domain/projectData/projectDataModel';
import type { Message } from 'shared/domain/messages/message';
import { projectDataSources } from './projectDataSources';

/** Inspection data of a role without inspections: nothing is ever downloaded, nothing to wait for. */
async function isWithoutAccess(key: ProjectDataKey): Promise<boolean> {
  return isInspectionDataKey(key) && !(await canSyncInspections());
}

/** What ended the wait, for the failure: `pullFailed` with its reason, or a collection without all items. */
export function describeDownloadEnd(message: FirstDownloadEnd): string {
  if (!message) {
    return 'downloaded before';
  }
  if (message === FIRST_DOWNLOAD_NOT_RUNNING) {
    return 'no download running';
  }
  if (message === FIRST_DOWNLOAD_SESSION_ENDED) {
    return 'a logout';
  }
  const { type, error, data } = message as Message & { error?: { message?: unknown }; data?: { hasAll?: unknown } };
  if (error) {
    return `${type}: ${String(error.message)}`;
  }
  return data && 'hasAll' in data ? `${type} (hasAll: ${String(data.hasAll)})` : type;
}

/** The download ended because the API refused the project's data: the user has no access to it. */
function endedWithoutProjectAccess(end: FirstDownloadEnd): boolean {
  return typeof end === 'object' && end !== null && isNoProjectAccessPullFailure(end);
}

/** How a wait for the first download ended. */
interface FirstDownloadWait {
  end: FirstDownloadEnd;
  isDownloaded: boolean;
  /**
   * The project data was replaced meanwhile (the scope is outdated, or the data that was downloaded
   * when the wait began is gone): the end says nothing about the current project's download.
   */
  isDataReplaced: boolean;
}

async function waitForFirstDownloadOfCurrentData(source: EntityReadStorage): Promise<FirstDownloadWait> {
  const scope = captureCollectionScope(source.service, undefined, source.contract.key);
  const end = await waitForFirstDownload(source);
  const isDownloaded = Boolean((await source.service.get())?.isDownloaded);
  // `null`: it was downloaded when the wait began, so a state that is not now was changed meanwhile (a flush).
  // A logout outdates the scope too, but nothing replaces the data then: waiting again would only time out.
  const isDataReplaced =
    !isDownloaded && end !== FIRST_DOWNLOAD_SESSION_ENDED && (end === null || !(await scope.isCurrent()));
  return { end, isDownloaded, isDataReplaced };
}

/**
 * Resolves once the data is downloaded, starting the download when missing.
 * A wait that ended while the project data was replaced starts again for the
 * current project's data. Throws when the download ended without the data
 * (a failed pull), naming what ended it; a `ProjectDataDownloadError` when the
 * API refused the project, so the page can tell a lost access.
 */
export async function ensureProjectDataDownloaded(key: ProjectDataKey): Promise<void> {
  if (await isWithoutAccess(key)) {
    return;
  }
  const source = projectDataSources[key];
  let wait = await waitForFirstDownloadOfCurrentData(source);
  for (let restarts = 0; wait.isDataReplaced && restarts < RESTARTS_FOR_REPLACED_DATA; restarts += 1) {
    wait = await waitForFirstDownloadOfCurrentData(source);
  }
  if (!wait.isDownloaded) {
    const replacedNote = wait.isDataReplaced ? ' (the project data kept being replaced)' : '';
    const message = `Project data download failed: ${key}, ended by ${describeDownloadEnd(wait.end)}${replacedNote}`;
    if (endedWithoutProjectAccess(wait.end)) {
      throw new ProjectDataDownloadError(message, ProjectDataDownloadFailure.noProjectAccess);
    }
    throw new Error(message);
  }
}

async function continueDownloadOf(key: ProjectDataKey): Promise<ProjectDataDownloadStatus> {
  if (await isWithoutAccess(key)) {
    return ProjectDataDownloadStatus.downloaded;
  }
  const source = projectDataSources[key];
  const serviceData = await source.service.get();
  if (serviceData?.isDownloaded) {
    return ProjectDataDownloadStatus.downloaded;
  }
  if (isPullRunning(source.contract)) {
    return ProjectDataDownloadStatus.downloading;
  }
  if (serviceData?.isDownloading) {
    // Left by a download this worker no longer runs (it was restarted): it blocks every sync.
    await source.service.stopDownloading();
  }
  syncWhenRequestsAllowed(source.sync);
  return ProjectDataDownloadStatus.idle;
}

/** Where each download stands; downloads that neither ended nor run are started again. */
export async function continueProjectDataDownload(keys: ProjectDataKey[]): Promise<ProjectDataDownloadProgress> {
  const statuses = await Promise.all(keys.map(async (key) => [key, await continueDownloadOf(key)] as const));
  return Object.fromEntries(statuses);
}

/** Syncs every kind of project data and the dictionaries from the API (with the permissions). */
export function syncAllProjectData(): void {
  syncWhenRequestsAllowed(() =>
    syncWithPermissions(() => {
      Object.values(projectDataSources).forEach(({ sync }) => sync());
      syncAllFieldValues();
    })
  );
}

export const init = (): void => {
  swSignals.staleProjectDataDetected.subscribe(syncAllProjectData);
};
